import { LinearUnit } from '../../../../../../utils/proj4/units';
import {
  getMapAreaMm,
  PageLayout,
  PageOrientation,
  PaperDimensions,
  PaperSize,
} from '../helpers/types';

/**
 * The page of the CAD drawing report. The scale is the denominator: 500 is 1:500, 1 mm on paper is
 * 500 mm on the ground. Feet projects use engineering scales, where 1" = 40' is 480.
 */
export type PageSettings = {
  layout: PageLayout;
  size: PaperSize;
  orientation: PageOrientation;
  scale: number;
};

export const defaultPageSettings: Omit<PageSettings, 'scale'> = {
  layout: 'cad-frame-1',
  size: 'A3',
  orientation: 'landscape',
};

const INCHES_PER_FOOT = 12;

const metricScales = [50, 100, 200, 400, 500, 1000, 2000, 4000];

/** Engineering scales, as feet to the inch: 1" = 10', 1" = 20', ... */
const feetPerInchScales = [10, 20, 30, 40, 50, 60, 100, 200, 500, 1000];

/** The predefined scales for a project's unit, as denominators, smallest first. */
export function getPresetScales(unit: LinearUnit): number[] {
  return unit === 'ft' ? feetPerInchScales.map((feet) => feet * INCHES_PER_FOOT) : metricScales;
}

/** The number the user types for a scale: the denominator, or feet to the inch in feet projects. */
export function scaleToInput(scale: number, unit: LinearUnit): number {
  return unit === 'ft' ? scale / INCHES_PER_FOOT : scale;
}

export function scaleFromInput(value: number, unit: LinearUnit): number {
  return unit === 'ft' ? value * INCHES_PER_FOOT : value;
}

/** The scale as written on the page: 1:500, or 1" = 40' in feet projects. */
export function formatScale(scale: number, unit: LinearUnit): string {
  return unit === 'ft' ? `1" = ${scaleToInput(scale, unit)}'` : `1:${scale}`;
}

/**
 * The size of the page's map area on the ground, in the coordinate system's unit. Meters per unit
 * is 1 for meters and 0.3048 for international feet, see getMetersPerUnit.
 */
export function getFrameSize(page: PageSettings, metersPerUnit: number): PaperDimensions {
  const mapArea = getMapAreaMm(page.layout, page.size, page.orientation);
  const groundPerMm = page.scale / 1000 / metersPerUnit;
  return { width: mapArea.width * groundPerMm, height: mapArea.height * groundPerMm };
}

/**
 * The largest predefined scale whose frame fits in the view, so the first frame covers about what
 * is shown. The smallest scale when none fits. The view's size is on the ground, in the coordinate
 * system's unit.
 */
export function getFittingScale(
  page: Omit<PageSettings, 'scale'>,
  view: PaperDimensions,
  unit: LinearUnit,
  metersPerUnit: number,
): number {
  const scales = getPresetScales(unit);
  const fitting = scales.filter((scale) => {
    const frame = getFrameSize({ ...page, scale }, metersPerUnit);
    return frame.width <= view.width && frame.height <= view.height;
  });

  return fitting.at(-1) ?? scales[0];
}
